import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MessageCircle } from "lucide-react";
import { ACADEMY, hasPhone, hasWhatsapp, whatsappLink } from "@/config/academy";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COACHING_MODES = ["Academy Physical Campus", "Online Classroom (Worldwide)"];

// Real batch timetable (owner-confirmed): Tue & Thu 5:00–7:00 PM,
// Sat & Sun 10:00 AM–12:00 PM.
const BATCH_SCHEDULE = {
  0: "10:00 AM – 12:00 PM", // Sunday
  2: "5:00 – 7:00 PM", // Tuesday
  4: "5:00 – 7:00 PM", // Thursday
  6: "10:00 AM – 12:00 PM", // Saturday
};

const getUpcomingSessions = (count = 6) => {
  const sessions = [];
  const day = new Date();
  day.setDate(day.getDate() + 1); // earliest bookable session is tomorrow
  while (sessions.length < count) {
    if (BATCH_SCHEDULE[day.getDay()]) sessions.push(new Date(day));
    day.setDate(day.getDate() + 1);
  }
  return sessions;
};

const SESSION_OPTIONS = getUpcomingSessions().map((date) => {
  const label = `${date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })} · ${BATCH_SCHEDULE[date.getDay()]}`;
  return { value: `${date.toISOString().split("T")[0]}|${label}`, label };
});

const inputClasses =
  "w-full px-4 py-3 rounded bg-emerald-950 border border-emerald-700 text-white placeholder-emerald-600 text-sm focus:outline-none focus:border-emerald-400";

const initialForm = {
  full_name: "",
  phone: "",
  coaching_mode: COACHING_MODES[0],
  preferred_slot: SESSION_OPTIONS[0].value,
  message: "",
};

const actionBaseClasses =
  "inline-flex items-center justify-center gap-2 px-5 py-3 rounded font-mono text-xs uppercase tracking-widest transition-colors";

// Call / WhatsApp quick actions: live links once the owner shares the numbers
// (src/config/academy.js); until then they render as clearly-marked placeholders.
const DirectActions = () => (
  <div className="mt-6" data-testid="contact-direct-actions">
    <div className="flex flex-col sm:flex-row gap-3">
      {hasPhone ? (
        <a href={`tel:${ACADEMY.phone}`} data-testid="call-button" className={`${actionBaseClasses} bg-white text-emerald-950 hover:bg-emerald-100`}>
          <Phone className="w-4 h-4" strokeWidth={2} />
          Call the Academy &middot; {ACADEMY.phoneDisplay || ACADEMY.phone}
        </a>
      ) : (
        <span data-testid="call-button" className={`${actionBaseClasses} border border-emerald-700 text-emerald-300 opacity-70 cursor-not-allowed`}>
          <Phone className="w-4 h-4" strokeWidth={2} />
          Call &middot; number coming soon
        </span>
      )}
      {hasWhatsapp ? (
        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-button"
          className={`${actionBaseClasses} bg-emerald-800 text-white hover:bg-emerald-700`}
        >
          <MessageCircle className="w-4 h-4" strokeWidth={2} />
          WhatsApp Us
        </a>
      ) : (
        <span data-testid="whatsapp-button" className={`${actionBaseClasses} border border-emerald-700 text-emerald-300 opacity-70 cursor-not-allowed`}>
          <MessageCircle className="w-4 h-4" strokeWidth={2} />
          WhatsApp &middot; number coming soon
        </span>
      )}
    </div>
    {!hasPhone && !hasWhatsapp && (
      <p className="mt-3 text-[11px] font-mono text-emerald-400">
        Direct line being set up &mdash; the form below reaches us today.
      </p>
    )}
  </div>
);

export const ContactSection = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const [preferred_date, preferred_time] = form.preferred_slot.split("|");
      const { preferred_slot, ...rest } = form;
      await axios.post(`${API}/enquiries`, { ...rest, preferred_date, preferred_time });
      setSubmitted(true);
      setForm(initialForm);
      toast.success("Trial request received. A FIDE coach will call you back shortly.");
    } catch (error) {
      toast.error("Could not submit your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-emerald-950 text-emerald-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="font-mono text-xs uppercase tracking-widest text-emerald-300 mb-2">Direct Intake</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight text-white leading-none mb-6">
              Schedule Your Free Evaluation
            </h2>
            <p className="text-emerald-200 text-sm leading-relaxed mb-6">
              Fill out the intake form below to book a free 45-minute diagnostic session with a FIDE coach
              or to ask about batch availability.
            </p>
            <div className="p-4 rounded bg-emerald-900/60 border border-emerald-800 text-xs font-mono text-emerald-300 space-y-2">
              <div>&#10003; Diagnostic game assessment</div>
              <div>&#10003; Honest batch placement recommendation</div>
              <div>&#10003; Both in-person &amp; online slots open</div>
              <div>&#10003; Batches: Tue &amp; Thu 5&ndash;7 PM &middot; Sat &amp; Sun 10 AM&ndash;12 PM</div>
            </div>
            <DirectActions />
          </div>

          <div className="lg:col-span-7">
            <div className="bg-emerald-900/90 p-8 rounded-xl border border-emerald-800 shadow-2xl">
              {submitted ? (
                <div className="text-center py-10" data-testid="enquiry-success-message">
                  <div className="font-mono text-xs uppercase tracking-widest text-emerald-300 mb-3">Received</div>
                  <h3 className="font-display text-3xl uppercase text-white mb-4">Your Trial Slot Is Requested</h3>
                  <p className="text-sm text-emerald-200 leading-relaxed max-w-md mx-auto">
                    A FIDE-rated coach will reach out on your phone / WhatsApp number to confirm your free
                    45-minute evaluation at your preferred slot.
                  </p>
                  <button
                    type="button"
                    data-testid="enquiry-another-button"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 px-6 py-3 rounded bg-white text-emerald-950 font-mono text-xs uppercase tracking-widest font-bold hover:bg-emerald-100 transition-colors"
                  >
                    Submit Another Enquiry
                  </button>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleSubmit} data-testid="enquiry-form">
                  <div>
                    <label htmlFor="full_name" className="block font-mono text-xs uppercase text-emerald-200 mb-1">
                      Full Name *
                    </label>
                    <input
                      id="full_name"
                      name="full_name"
                      type="text"
                      required
                      minLength={2}
                      placeholder="Your Name"
                      value={form.full_name}
                      onChange={handleChange}
                      className={inputClasses}
                      data-testid="enquiry-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block font-mono text-xs uppercase text-emerald-200 mb-1">
                        Phone / WhatsApp *
                      </label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        minLength={6}
                        placeholder="Phone Number"
                        value={form.phone}
                        onChange={handleChange}
                        className={inputClasses}
                        data-testid="enquiry-phone-input"
                      />
                    </div>
                    <div>
                      <label htmlFor="coaching_mode" className="block font-mono text-xs uppercase text-emerald-200 mb-1">
                        Coaching Mode *
                      </label>
                      <select
                        id="coaching_mode"
                        name="coaching_mode"
                        value={form.coaching_mode}
                        onChange={handleChange}
                        className={inputClasses}
                        data-testid="enquiry-mode-select"
                      >
                        {COACHING_MODES.map((mode) => (
                          <option key={mode} value={mode}>
                            {mode}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="preferred_slot" className="block font-mono text-xs uppercase text-emerald-200 mb-1">
                      Choose Your Trial Slot *
                    </label>
                    <select
                      id="preferred_slot"
                      name="preferred_slot"
                      value={form.preferred_slot}
                      onChange={handleChange}
                      className={inputClasses}
                      data-testid="enquiry-slot-select"
                    >
                      {SESSION_OPTIONS.map((slot) => (
                        <option key={slot.value} value={slot.value}>
                          {slot.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-[11px] font-mono text-emerald-400">
                      Tue &amp; Thu 5:00&ndash;7:00 PM &middot; Sat &amp; Sun 10:00 AM&ndash;12:00 PM
                    </p>
                  </div>

                  <div>
                    <label htmlFor="message" className="block font-mono text-xs uppercase text-emerald-200 mb-1">
                      Message / Student Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={3}
                      placeholder="Student age, previous experience, tournament ambitions..."
                      value={form.message}
                      onChange={handleChange}
                      className={inputClasses}
                      data-testid="enquiry-message-textarea"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="enquiry-submit-button"
                    className="w-full py-4 rounded bg-white text-emerald-950 font-mono text-xs uppercase tracking-widest font-bold hover:bg-emerald-100 transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Submitting..." : "Submit Enquiry & Book Free Trial"}
                  </button>

                  <p className="text-[11px] font-mono text-emerald-400 text-center mt-2">
                    The coach confirms your exact slot on call / WhatsApp.
                    We never share your contact details.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
